import { useContext, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import cx from "classnames";
import { ThemeContext } from "./components/Theme";
import { FeatureFlagOverrides } from "./v2/components/feature-flag/FeatureFlagOverrides";
import { isQuack } from "./v2/components/feature-flag/FeatureFlagContext";
import {
  PageLayout,
  PageHeader,
  InnerContentLayout,
} from "./v2/components/layout";
import styles from "./App.module.scss";
import { Flex, Button, Text, Box } from "@radix-ui/themes";
import { Kanban } from "./v2/assets/icons/Kanban";
import { PopoverWithDefaults } from "./v2/components/popover/popover-with-defaults";
import { HoverCard } from "./v2/components/hover-card/HoverCard";
import { Tabs, TabItem } from "./v2/components/tabs/Tabs";
import { useTabs } from "./v2/components/tabs/useTabs";
import {
  Tabs as DataTabs,
  TabsList as DataTabsList,
  TabsTrigger as DataTabsTrigger,
  TabsContent as DataTabsContent,
} from "./v2/components/dataTabs/tabs";

const tabs: TabItem[] = [
  { value: "overview", label: "Overview" },
  { value: "analytics", label: "Analytics" },
  { value: "reports", label: "Reports" },
];

function DummyPage() {
  const { selectedTab, onTabChange } = useTabs({ defaultTab: "overview" });
  const { selectedTab: selectedTab2, onTabChange: onTabChange2 } = useTabs({
    defaultTab: "overview",
    paramKey: "mor",
  });

  return (
    <PageLayout>
      <PageHeader
        title="New App Layout"
        icon={<Kanban fill="gray" />}
        description="New App Layout"
      />

      <InnerContentLayout>
        <Tabs
          tabs={tabs}
          value={selectedTab}
          onValueChange={onTabChange}
          fullWidth
        />

        <Tabs
          variant="data"
          tabs={tabs}
          value={selectedTab2}
          onValueChange={onTabChange2}
          fullWidth
        />

        {selectedTab === "overview" && (
          <Box>
            <PopoverWithDefaults
              trigger={
                <Button variant="solid" color="gray" highContrast>
                  Click me for popover
                </Button>
              }
              title="Popover Title"
              description="This is the content inside the popover. You can add any content here."
              buttonText="Take Action"
              onButtonClick={() => {}}
              buttonVariant="outline"
              buttonColor="gray"
              buttonHighContrast={true}
            />

            <HoverCard
              trigger={
                <Button variant="outline" color="gray" highContrast>
                  Hover me for card
                </Button>
              }
              title="Hover Card Title"
              description="This content appears when you hover over the button. No clicking required!"
              buttonText="Learn More"
              onButtonClick={() => {}}
              buttonVariant="outline"
              buttonColor="gray"
              buttonHighContrast={true}
            />
          </Box>
        )}

        {selectedTab === "analytics" && (
          <Box>
            <div
              style={{
                border: "1px solid var(--gray-6)",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Text size="3" weight="medium">
                Analytics Content
              </Text>
              <Text size="2" color="gray">
                This is the analytics tab content.
              </Text>
            </div>
          </Box>
        )}

        {selectedTab === "reports" && (
          <Box>
            <div
              style={{
                border: "1px solid var(--gray-6)",
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <Text size="3" weight="medium">
                Reports Content
              </Text>
              <Text size="2" color="gray">
                This is the reports tab content.
              </Text>
            </div>
          </Box>
        )}

        <DataTabs defaultValue="data-overview">
          <DataTabsList>
            <DataTabsTrigger value="data-overview">
              Data Overview
            </DataTabsTrigger>
            <DataTabsTrigger value="data-details">Data Details</DataTabsTrigger>
          </DataTabsList>
          <DataTabsContent value="data-overview">
            <div className="rounded-lg border p-4">
              <Text size="3" weight="medium">
                Data Overview Content
              </Text>
              <Text size="2" color="gray">
                This is the data overview tab content using dataTabs component.
              </Text>
            </div>
          </DataTabsContent>
          <DataTabsContent value="data-details">
            <div className="rounded-lg border p-4">
              <Text size="3" weight="medium">
                Data Details Content
              </Text>
              <Text size="2" color="gray">
                This is the data details tab content using dataTabs component.
              </Text>
            </div>
          </DataTabsContent>
        </DataTabs>
      </InnerContentLayout>
    </PageLayout>
  );
}

function AppV2() {
  const { user } = useAuth0();
  const { isDarkMode } = useContext(ThemeContext);

  const isQuackUser = useMemo(() => isQuack(user), [user]);

  return (
    <Flex
      width="100dvw"
      height="100dvh"
      className={cx({
        [styles.dark_mode]: isDarkMode,
        [styles.light_mode]: !isDarkMode,
      })}
    >
      {isQuackUser && <FeatureFlagOverrides />}

      <Flex flexGrow={"1"}>
        <DummyPage />
      </Flex>
    </Flex>
  );
}

export default AppV2;
